import { mdiCalendar } from '@mdi/js';
import * as React from 'react';
import { format as dateFnFormat, parse, isValid } from 'date-fns';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { Popover } from '../popover';
import { Tooltip } from '../tooltip';
import * as S from './date-icon-select.styles';

export interface IDateIconSelectProps {
    value?: string; // Format: yyyy-MM-dd
    onChange: (date: string | undefined) => void;
    disabled?: boolean;
    className?: string;
    tooltip?: string; // Tooltip text to display on hover
}

export const DateIconSelect: React.FunctionComponent<IDateIconSelectProps> = (props: IDateIconSelectProps): React.ReactElement => {
    const { value, onChange, disabled, className, tooltip } = props;
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [currentMonth, setCurrentMonth] = React.useState<Date>(new Date());

    // Parse the value string to a Date object for DayPicker
    const selectedDate = React.useMemo(() => {
        if (!value) return undefined;
        const parsed = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsed) ? parsed : undefined;
    }, [value]);

    const handleDaySelect = (date: Date | undefined) => {
        if (!date) {
            onChange(undefined);
        } else {
            // Format date as yyyy-MM-dd
            const formatted = dateFnFormat(date, 'yyyy-MM-dd');
            onChange(formatted);
            setMenuOpen(false);
        }
    };

    const renderButton = (): React.ReactElement => {
        return (
            <S.DateButton disabled={disabled} aria-label="Select date">
                <S.DateButtonIcon path={mdiCalendar} />
            </S.DateButton>
        );
    };

    const dayPickerProps: DayPickerProps = {
        selected: selectedDate,
        month: currentMonth,
        mode: 'single',
        onMonthChange: setCurrentMonth,
        onSelect: handleDaySelect,
        showOutsideDays: true,
    };

    const popover = (
        <Popover
            renderButton={renderButton}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            align="right"
            verticalAlign="auto"
            className={className}>
            <S.PopoverContainer>
                <DayPicker {...dayPickerProps} />
            </S.PopoverContainer>
        </Popover>
    );

    if (tooltip) {
        return (
            <Tooltip direction="top" title={<span>{tooltip}</span>}>
                {popover}
            </Tooltip>
        );
    }

    return popover;
};
